import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaDownload, FaPrint, FaShareAlt } from 'react-icons/fa';
import html2pdf from 'html2pdf.js';
import './Report.css';

const ReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sourceAssessment, setSourceAssessment] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await axios.get(`/api/reports/${id}`);
        setReport(res.data);
        
        // If the report has an assessmentId, store it
        if (res.data && res.data.assessmentId) {
          setSourceAssessment(res.data.assessmentId);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching report:', err);
        setError('Failed to load report');
        setLoading(false);
      }
    };

    fetchReport();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };
  
  const handleDownload = async (report) => {
    try {
      if (report.format === 'pdf' || report.format === 'application/pdf') {
        // For PDF reports, fetch the PDF directly
        const res = await axios.get(`/api/reports/download/${report._id}`, {
          responseType: 'blob'
        });
        
        // Create a blob URL and trigger download
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        
        // Create a temporary link and click it to download
        const link = document.createElement('a');
        link.href = url;
        link.download = `${report.title.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the blob URL
        window.URL.revokeObjectURL(url);
      } else {
        // For HTML reports, generate a PDF using html2pdf
        if (!report.content) {
          throw new Error('No content available for this report');
        }
        
        // Show loading message
        setError('Generating PDF, please wait...');
        
        // For Findings & Recommendations report, we need to handle the special page structure
        if (report.type === 'Findings & Recommendations') {
          // Create a clone of the report content to modify for PDF generation
          const reportContent = document.querySelector('.findings-recommendations');
          if (!reportContent) {
            throw new Error('Report content element not found');
          }
          
          // Configure html2pdf options with special handling for page breaks
          const options = {
            margin: [10, 10, 10, 10],
            filename: `${report.title.replace(/\s+/g, '_')}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { 
              scale: 2, 
              useCORS: true,
              logging: true,
              letterRendering: true
            },
            jsPDF: { 
              unit: 'mm', 
              format: 'letter', 
              orientation: 'portrait',
              compress: true
            },
            pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
          };
          
          // Generate and download the PDF
          html2pdf()
            .set(options)
            .from(reportContent)
            .toPdf() // Convert to PDF
            .get('pdf') // Get the PDF object
            .then((pdf) => {
              // Save the PDF
              pdf.save(`${report.title.replace(/\s+/g, '_')}.pdf`);
              setError(''); // Clear any error messages
            })
            .catch(err => {
              console.error('Error generating PDF:', err);
              setError('Failed to generate PDF: ' + (err.message || 'Unknown error'));
            });
        } else {
          // For other report types, use standard approach
          const reportContent = document.querySelector('.report-content');
          if (!reportContent) {
            throw new Error('Report content element not found');
          }
          
          // Configure html2pdf options
          const options = {
            margin: 10,
            filename: `${report.title.replace(/\s+/g, '_')}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' }
          };
          
          // Generate and download the PDF
          html2pdf()
            .set(options)
            .from(reportContent)
            .save()
            .then(() => {
              setError(''); // Clear any error messages
            })
            .catch(err => {
              console.error('Error generating PDF:', err);
              setError('Failed to generate PDF: ' + (err.message || 'Unknown error'));
            });
        }
      }
    } catch (err) {
      console.error('Error downloading report:', err);
      setError('Failed to download report: ' + (err.message || 'Unknown error'));
    }
  };

  const formatReportContent = (content) => {
    // If content is undefined or null (for PDF reports), return a message
    if (!content) {
      return (
        <div className="no-content-message">
          <p>No content available for this report.</p>
        </div>
      );
    }
    
    try {
      // Check if content is JSON string (for Findings & Recommendations)
      if (content.startsWith('{') && content.endsWith('}')) {
        try {
          const jsonContent = JSON.parse(content);
          
          // Handle Findings & Recommendations format
          if (jsonContent.categories) {
            // Get assessment date
            const reportDate = report.createdAt ? new Date(report.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }) : new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
            
            // Generate table of contents items
            const tableOfContents = [
              { title: 'Executive Summary', page: 2 },
              { title: 'About the Assessment', page: 3 },
              { title: 'Overview - Strengths', page: 4 },
              { title: 'Overview - Areas for Improvement', page: 5 }
            ];
            
            // Add categories to table of contents
            let currentPage = 6;
            const categoryTocItems = Object.entries(jsonContent.categories)
              .filter(([, data]) => data.score > 0) // Only include categories with scores > 0
              .map(([category]) => {
                const item = { title: category, page: currentPage };
                currentPage += 2; // Estimate 2 pages per category
                return item;
              });
            
            // Calculate overall maturity score
            const categoryScores = Object.values(jsonContent.categories)
              .filter(cat => cat.score > 0) // Only include categories with scores > 0
              .map(cat => cat.score);
            const overallScore = categoryScores.length > 0 
              ? Math.round(categoryScores.reduce((sum, score) => sum + score, 0) / categoryScores.length) 
              : 0;
            
            // Determine overall maturity level
            let overallMaturityLevel = '';
            if (overallScore < 1) overallMaturityLevel = 'None';
            else if (overallScore < 20) overallMaturityLevel = 'Initial';
            else if (overallScore < 50) overallMaturityLevel = 'Defined';
            else if (overallScore < 75) overallMaturityLevel = 'Managed';
            else if (overallScore < 91) overallMaturityLevel = 'Compliant';
            else overallMaturityLevel = 'Optimized';
            
            // Create one-page summary
            const summaryContent = `
              <p>This report provides a comprehensive assessment of your organization's resilience maturity across key categories. 
              Based on your responses, your organization has achieved an overall maturity score of <strong>${overallScore}%</strong>, 
              placing it at the <strong>${overallMaturityLevel}</strong> level.</p>
              
              <p>The assessment identified the following key strengths and areas for improvement:</p>
            `;
            
            return (
              <div className="findings-recommendations">
                {/* Cover Page */}
                <div className="report-page cover-page" data-page="1">
                  <div style={{ textAlign: 'center', marginTop: '2in' }}>
                    <div style={{ 
                      backgroundColor: '#243444', 
                      color: '#FFFAFA', 
                      display: 'inline-block',
                      padding: '10px 30px',
                      marginBottom: '30px'
                    }}>
                      <h1 style={{ margin: 0, color: '#FFFAFA' }}>FINDINGS & RECOMMENDATIONS</h1>
                    </div>
                    <h2 style={{ color: '#243444', marginBottom: '50px' }}>{report.title.replace(' - Findings & Recommendations', '')}</h2>
                    <div style={{ 
                      width: '100px', 
                      height: '100px', 
                      backgroundColor: '#D2BA7B', 
                      margin: '50px auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{ 
                        width: '80px', 
                        height: '80px', 
                        backgroundColor: '#243444',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#FFFAFA',
                        fontSize: '24px',
                        fontWeight: 'bold'
                      }}>
                        {overallScore}%
                      </div>
                    </div>
                    <h3 style={{ color: '#4C5454' }}>Overall Maturity: {overallMaturityLevel}</h3>
                  </div>
                  
                  <div style={{ marginBottom: '1in' }}>
                    <p style={{ textAlign: 'center', color: '#4C5454' }}>
                      <strong>Generated on:</strong> {reportDate}<br />
                      <strong>Business Resilience Navigator</strong>
                    </p>
                  </div>
                </div>
                
                {/* Table of Contents */}
                <div className="report-page table-of-contents" data-page="2">
                  <h2 style={{ color: '#243444', borderBottom: '2px solid #D2BA7B', paddingBottom: '10px' }}>TABLE OF CONTENTS</h2>
                  
                  <div style={{ marginTop: '30px' }}>
                    {[...tableOfContents, ...categoryTocItems].map((item, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        margin: '10px 0',
                        borderBottom: '1px dotted #4C5454'
                      }}>
                        <span style={{ color: '#243444', fontWeight: index === 0 ? 'bold' : 'normal' }}>{item.title}</span>
                        <span style={{ color: '#4C5454' }}>{item.page}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* About the Assessment */}
                <div className="report-page about-assessment" data-page="3">
                  <h2 style={{ color: '#243444', borderBottom: '2px solid #D2BA7B', paddingBottom: '10px' }}>ABOUT THE ASSESSMENT</h2>
                  
                  <div style={{ marginTop: '30px', color: '#4C5454', lineHeight: '1.6' }}>
                    <h3 style={{ color: '#243444', marginBottom: '15px' }}>Purpose</h3>
                    <p>
                      The Business Resilience Navigator assessment provides a comprehensive evaluation of your organization's resilience maturity across key categories. 
                      This assessment helps identify strengths and areas for improvement in your organization's ability to prepare for, respond to, and recover from disruptions.
                    </p>
                    
                    <h3 style={{ color: '#243444', marginTop: '25px', marginBottom: '15px' }}>Maturity Levels</h3>
                    <p>The assessment uses the following maturity levels to evaluate your organization's resilience capabilities:</p>
                    
                    <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                      <div style={{ display: 'flex', marginBottom: '15px', alignItems: 'center' }}>
                        <div style={{ width: '120px', backgroundColor: '#7C1227', color: 'white', padding: '5px 10px', textAlign: 'center', fontWeight: 'bold', marginRight: '15px' }}>None (0%)</div>
                        <div>No resilience capabilities have been implemented in this area.</div>
                      </div>
                      
                      <div style={{ display: 'flex', marginBottom: '15px', alignItems: 'center' }}>
                        <div style={{ width: '120px', backgroundColor: '#F44336', color: 'white', padding: '5px 10px', textAlign: 'center', fontWeight: 'bold', marginRight: '15px' }}>Initial (1-19%)</div>
                        <div>Basic resilience capabilities exist but are ad hoc, reactive, and not formally documented.</div>
                      </div>
                      
                      <div style={{ display: 'flex', marginBottom: '15px', alignItems: 'center' }}>
                        <div style={{ width: '120px', backgroundColor: '#FF9800', color: 'white', padding: '5px 10px', textAlign: 'center', fontWeight: 'bold', marginRight: '15px' }}>Defined (20-49%)</div>
                        <div>Resilience capabilities are defined and documented but may not be consistently implemented.</div>
                      </div>
                      
                      <div style={{ display: 'flex', marginBottom: '15px', alignItems: 'center' }}>
                        <div style={{ width: '120px', backgroundColor: '#FFC107', color: 'white', padding: '5px 10px', textAlign: 'center', fontWeight: 'bold', marginRight: '15px' }}>Managed (50-74%)</div>
                        <div>Resilience capabilities are well-defined, implemented consistently, and regularly monitored.</div>
                      </div>
                      
                      <div style={{ display: 'flex', marginBottom: '15px', alignItems: 'center' }}>
                        <div style={{ width: '120px', backgroundColor: '#4CAF50', color: 'white', padding: '5px 10px', textAlign: 'center', fontWeight: 'bold', marginRight: '15px' }}>Compliant (75-90%)</div>
                        <div>Resilience capabilities are fully implemented, regularly measured, and continuously improved.</div>
                      </div>
                      
                      <div style={{ display: 'flex', marginBottom: '15px', alignItems: 'center' }}>
                        <div style={{ width: '120px', backgroundColor: '#2E7D32', color: 'white', padding: '5px 10px', textAlign: 'center', fontWeight: 'bold', marginRight: '15px' }}>Optimized (91-100%)</div>
                        <div>Resilience capabilities are industry-leading, fully integrated into business processes, and continuously optimized.</div>
                      </div>
                    </div>
                    
                    <h3 style={{ color: '#243444', marginTop: '25px', marginBottom: '15px' }}>Scoring Methodology</h3>
                    <p>
                      The assessment scores are calculated as follows:
                    </p>
                    <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
                      <li>Each question within a category is scored based on your organization's capabilities.</li>
                      <li>Category scores are calculated by averaging the scores of all questions within that category.</li>
                      <li>The overall maturity score is calculated by averaging all category scores.</li>
                    </ul>
                    
                    <h3 style={{ color: '#243444', marginTop: '25px', marginBottom: '15px' }}>Using This Report</h3>
                    <p>
                      This report provides a detailed analysis of your organization's resilience maturity, including:
                    </p>
                    <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
                      <li>An executive summary with your overall maturity score and level</li>
                      <li>Key strengths and areas for improvement</li>
                      <li>Detailed findings and recommendations for each category</li>
                    </ul>
                    <p style={{ marginTop: '15px' }}>
                      Use these insights to develop a roadmap for enhancing your organization's resilience capabilities and prioritize improvement initiatives.
                    </p>
                  </div>
                </div>
                
                {/* Executive Summary */}
                <div className="report-page executive-summary" data-page="4">
                  <h2 style={{ color: '#243444', borderBottom: '2px solid #D2BA7B', paddingBottom: '10px' }}>EXECUTIVE SUMMARY</h2>
                  
                  <div style={{ marginTop: '30px' }}>
                    <div dangerouslySetInnerHTML={{ __html: summaryContent }} />
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px', flexWrap: 'wrap' }}>
                      <div style={{ width: '48%', minWidth: '300px', marginBottom: '20px' }}>
                        <h3 style={{ color: '#243444', backgroundColor: '#D2BA7B', padding: '5px 10px', display: 'inline-block' }}>Key Strengths</h3>
                        <ul style={{ color: '#4C5454', marginTop: '10px' }}>
                          {jsonContent.strengths && jsonContent.strengths.length > 0 ? 
                            jsonContent.strengths.slice(0, 5).map((strength, index) => (
                              <li key={index}>{strength}</li>
                            )) : 
                            <li>No significant strengths identified.</li>
                          }
                        </ul>
                      </div>
                      
                      <div style={{ width: '48%', minWidth: '300px' }}>
                        <h3 style={{ color: '#243444', backgroundColor: '#D2BA7B', padding: '5px 10px', display: 'inline-block' }}>Areas for Improvement</h3>
                        <ul style={{ color: '#4C5454', marginTop: '10px' }}>
                          {jsonContent.weaknesses && jsonContent.weaknesses.length > 0 ? 
                            jsonContent.weaknesses.slice(0, 5).map((weakness, index) => (
                              <li key={index}>{weakness}</li>
                            )) : 
                            <li>No significant weaknesses identified.</li>
                          }
                        </ul>
                      </div>
                    </div>
                    
                    <div style={{ marginTop: '50px', clear: 'both' }}>
                      <h3 style={{ color: '#243444', backgroundColor: '#D2BA7B', padding: '5px 10px', display: 'inline-block', marginBottom: '20px' }}>Category Maturity Levels</h3>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'flex-start' }}>
                        {Object.entries(jsonContent.categories)
                          .filter(([, data]) => data.score > 0) // Only include categories with scores > 0
                          .map(([category, data], index) => (
                          <div key={index} style={{ 
                            width: 'calc(33% - 20px)', 
                            minWidth: '200px',
                            marginBottom: '20px',
                            textAlign: 'center'
                          }}>
                            <div style={{ 
                              backgroundColor: data.score >= 75 ? '#4CAF50' : data.score >= 50 ? '#FFC107' : '#F44336',
                              color: 'white',
                              padding: '10px',
                              borderRadius: '4px',
                              marginBottom: '10px'
                            }}>
                              {data.score}%
                            </div>
                            <div style={{ fontSize: '14px', color: '#4C5454', wordWrap: 'break-word' }}>{category}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Overview Section - Strengths */}
                <div className="report-page overview-section" data-page="5">
                  <h2 style={{ color: '#243444', borderBottom: '2px solid #D2BA7B', paddingBottom: '10px' }}>OVERVIEW</h2>
                  
                  <div className="strengths-section" style={{ marginTop: '20px' }}>
                    <h3 style={{ color: '#243444', backgroundColor: '#D2BA7B', padding: '5px 10px', display: 'inline-block', marginBottom: '15px' }}>Key Strengths</h3>
                    
                    {/* Generate strengths based on category scores */}
                    {(() => {
                      // Get top 3 categories by score
                      const topCategories = Object.entries(jsonContent.categories)
                        .filter(([, data]) => data.score > 0)
                        .sort(([, dataA], [, dataB]) => dataB.score - dataA.score)
                        .slice(0, 3);
                      
                      if (topCategories.length > 0) {
                        return (
                          <ul style={{ color: '#4C5454' }}>
                            {topCategories.map(([category, data], index) => (
                              <li key={index} style={{ marginBottom: '10px' }}>
                                {category} demonstrates strength with a maturity score of {data.score}%.
                              </li>
                            ))}
                          </ul>
                        );
                      } else {
                        return (
                          <div style={{ color: '#4C5454', fontStyle: 'italic', marginTop: '15px' }}>
                            <p>Based on the assessment results, no significant strengths were identified that meet the criteria for highlighting. This indicates an opportunity to develop foundational resilience capabilities across all categories.</p>
                            <p>Organizations typically begin their resilience journey by establishing basic capabilities in governance, risk management, and business continuity planning.</p>
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
                
                {/* Overview Section - Areas for Improvement */}
                <div className="report-page overview-section" data-page="6">
                  <h2 style={{ color: '#243444', borderBottom: '2px solid #D2BA7B', paddingBottom: '10px' }}>OVERVIEW - AREAS FOR IMPROVEMENT</h2>
                  
                  <div className="weaknesses-section" style={{ marginTop: '20px' }}>
                    <h3 style={{ color: '#243444', backgroundColor: '#D2BA7B', padding: '5px 10px', display: 'inline-block', marginBottom: '15px' }}>Areas for Improvement</h3>
                    
                    {/* Generate areas for improvement based on category scores */}
                    {(() => {
                      // Get bottom 3 categories by score
                      const bottomCategories = Object.entries(jsonContent.categories)
                        .filter(([, data]) => data.score > 0)
                        .sort(([, dataA], [, dataB]) => dataA.score - dataB.score)
                        .slice(0, 3);
                      
                      if (bottomCategories.length > 0) {
                        return (
                          <ul style={{ color: '#4C5454' }}>
                            {bottomCategories.map(([category, data], index) => (
                              <li key={index} style={{ marginBottom: '10px' }}>
                                {category} needs improvement with a maturity score of {data.score}%.
                              </li>
                            ))}
                          </ul>
                        );
                      } else {
                        return (
                          <div style={{ color: '#4C5454', fontStyle: 'italic', marginTop: '15px' }}>
                            <p>The assessment did not identify any significant areas for improvement that meet the criteria for highlighting. This suggests your organization has developed mature resilience capabilities across all categories.</p>
                            <p>Even with high maturity scores, organizations should continue to refine and optimize their resilience capabilities to maintain their effectiveness over time.</p>
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
                
                {/* Detailed Category Analysis */}
                <div className="categories-section">
                  {Object.entries(jsonContent.categories).map(([category, data], index) => {
                    // Create two pages for each category - one for findings and one for recommendations
                    const pageNumber = 6 + (index * 2);
                    return (
                      <React.Fragment key={index}>
                        {/* Findings Page */}
                        <div className="report-page category-section" data-page={pageNumber}>
                          <h2 style={{ 
                            color: '#243444', 
                            borderBottom: '2px solid #D2BA7B', 
                            paddingBottom: '10px',
                            marginBottom: '20px'
                          }}>{category.toUpperCase()}</h2>
                          <div style={{ 
                            backgroundColor: data.score >= 75 ? '#4CAF50' : data.score >= 50 ? '#FFC107' : '#F44336',
                            color: 'white',
                            padding: '5px 10px',
                            borderRadius: '4px',
                            display: 'inline-block',
                            marginBottom: '25px'
                          }}>
                            {/* Calculate maturity level based on score */}
                            Maturity Level: {(() => {
                              if (data.score < 1) return 'None';
                              else if (data.score < 20) return 'Initial';
                              else if (data.score < 50) return 'Defined';
                              else if (data.score < 75) return 'Managed';
                              else if (data.score < 91) return 'Compliant';
                              else return 'Optimized';
                            })()} ({data.score}%)
                          </div>
                          
                          <div className="findings-section">
                            <h3 style={{ color: '#243444', backgroundColor: '#D2BA7B', padding: '5px 10px', display: 'inline-block', marginBottom: '15px' }}>Findings</h3>
                            <div style={{ color: '#4C5454', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: data.findings }} />
                          </div>
                        </div>
                        
                        {/* Recommendations Page */}
                        <div className="report-page category-section" data-page={pageNumber + 1}>
                          <h2 style={{ 
                            color: '#243444', 
                            borderBottom: '2px solid #D2BA7B', 
                            paddingBottom: '10px',
                            marginBottom: '20px'
                          }}>{category.toUpperCase()} - RECOMMENDATIONS</h2>
                          
                          <div className="recommendations-section">
                            <h3 style={{ color: '#243444', backgroundColor: '#D2BA7B', padding: '5px 10px', display: 'inline-block', marginBottom: '15px' }}>Recommendations</h3>
                            <div style={{ color: '#4C5454', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: data.recommendations }} />
                          </div>
                      
                          {/* Removed Specific Improvement Areas section as requested */}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
                
                {/* Footer */}
                <div style={{ 
                  marginTop: '50px', 
                  borderTop: '1px solid #4C5454', 
                  paddingTop: '10px', 
                  textAlign: 'center',
                  color: '#4C5454',
                  fontSize: '12px'
                }}>
                  <p>© {new Date().getFullYear()} Business Resilience Navigator. All rights reserved.</p>
                </div>
              </div>
            );
          }
        } catch (jsonError) {
          console.error('Error parsing JSON content:', jsonError);
          // If JSON parsing fails, fall back to regular content formatting
        }
      }
      
      // For HTML content
      if (content.includes('<h') || content.includes('<p>') || content.includes('<div>')) {
        return <div dangerouslySetInnerHTML={{ __html: content }} />;
      }
      
      // For plain text content, split by newlines and convert to paragraphs
      return content.split('\n').map((paragraph, index) => {
        // Check if this is a heading (starts with # or ##)
        if (paragraph.startsWith('# ')) {
          return <h2 key={index}>{paragraph.substring(2)}</h2>;
        } else if (paragraph.startsWith('## ')) {
          return <h3 key={index}>{paragraph.substring(3)}</h3>;
        } else if (paragraph.startsWith('- ')) {
          // This is a bullet point
          return (
            <div key={index} className="bullet-point">
              <span className="bullet">•</span>
              <span>{paragraph.substring(2)}</span>
            </div>
          );
        } else if (paragraph.trim() === '') {
          // Empty line
          return <br key={index} />;
        } else {
          // Regular paragraph
          return <p key={index}>{paragraph}</p>;
        }
      });
    } catch (error) {
      console.error('Error formatting report content:', error);
      return (
        <div className="error-message">
          <p>There was an error displaying this report. Please try downloading it instead.</p>
        </div>
      );
    }
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!report) {
    return <div className="alert alert-danger">Report not found</div>;
  }

  return (
    <div className="report-detail">
      <div className="page-header">
        <div>
          <button 
            onClick={() => {
              // If we have a source assessment, go back to it
              if (sourceAssessment) {
                navigate(`/assessments/${sourceAssessment}`);
              } else {
                // Otherwise go to the reports list
                navigate('/reports');
              }
            }} 
            className="btn btn-light back-button"
          >
            <FaArrowLeft /> {sourceAssessment ? 'Back to Assessment' : 'Back to Reports'}
          </button>
          <h1>{report.title}</h1>
          <div className="report-meta">
            <div className={`report-type-badge ${report.type.toLowerCase().replace(/\s+/g, '-')}`}>
              {report.type}
            </div>
            <div className="report-date">
              Generated on {new Date(report.createdAt).toLocaleDateString()}
            </div>
          </div>
        </div>
        <div className="report-actions">
          <button onClick={handlePrint} className="btn btn-light">
            <FaPrint /> Print
          </button>
          <button 
            onClick={() => handleDownload(report)} 
            className="btn btn-light"
            style={{ backgroundColor: '#4C5454', color: '#FFFAFA', borderColor: '#4C5454' }}
          >
            <FaDownload /> Download
          </button>
          <button className="btn btn-light">
            <FaShareAlt /> Share
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="report-content card">
        <div className="report-body">
          {report.format === 'pdf' || report.format === 'application/pdf' ? (
            <div className="pdf-report-message" style={{ textAlign: 'center', padding: '30px' }}>
              <h3 style={{ color: '#243444' }}>PDF Report</h3>
              <p>This report is in PDF format and can be viewed by downloading it.</p>
              <button 
                onClick={() => handleDownload(report)} 
                className="btn btn-primary"
                style={{ backgroundColor: '#243444', borderColor: '#243444', margin: '15px' }}
              >
                <FaDownload /> Download PDF
              </button>
            </div>
          ) : (
            formatReportContent(report.content)
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportDetail;
